import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, addDoc, getDocs, query, where, updateDoc, deleteDoc, serverTimestamp, deleteField, runTransaction } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { placeIdLookup } from '../utils/placeIdLookup';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyCxmLt3Ekh9AE-0XOlrSG_kms-jw2ONGes",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "sfa-system-8a445.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "sfa-system-8a445",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "sfa-system-8a445.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "573433845624",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:573433845624:web:ea56fd216f28623dbb1a8d",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-V1KDTYFTMZ"
};

// Log current configuration for debugging (remove in production)
if (process.env.NODE_ENV === 'development') {
  console.log('🔥 Firebase Config:', {
    environment: process.env.REACT_APP_ENVIRONMENT,
    projectId: firebaseConfig.projectId,
    authDomain: firebaseConfig.authDomain
  });
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Tally-specific Firebase instance (LIVE project for Tally sync operations)
let tallyDb: any = null;
if (process.env.REACT_APP_TALLY_FIREBASE_PROJECT_ID && 
    process.env.REACT_APP_TALLY_FIREBASE_PROJECT_ID !== firebaseConfig.projectId) {
  const tallyConfig = {
    apiKey: process.env.REACT_APP_TALLY_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_TALLY_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_TALLY_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_TALLY_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_TALLY_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_TALLY_FIREBASE_APP_ID
  };
  const tallyApp = initializeApp(tallyConfig, 'tally');
  tallyDb = getFirestore(tallyApp);
  console.log('🔧 Tally Firebase initialized:', tallyConfig.projectId);
}

// Export function to get Tally Firestore (falls back to primary if not configured)
export const getTallyDb = () => tallyDb || db;

// Auth providers
const googleProvider = new GoogleAuthProvider();

// Auth functions
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    throw error;
  }
};

export const logOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    throw error;
  }
};

// Firestore helper functions
export const createDocument = async (collectionName: string, data: any, customDocId?: string) => {
  try {
    if (customDocId) {
      // Use setDoc with custom document ID
      const docRef = doc(db, collectionName, customDocId);
      await setDoc(docRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return customDocId;
    } else {
      // Use addDoc for auto-generated ID
      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    }
  } catch (error) {
    throw error;
  }
};

export const getDocument = async (collectionName: string, docId: string) => {
  try {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
};

export const updateDocument = async (collectionName: string, docId: string, data: any) => {
  try {
    const docRef = doc(db, collectionName, docId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw error;
  }
};

export const deleteDocument = async (collectionName: string, docId: string) => {
  try {
    const docRef = doc(db, collectionName, docId);
    await deleteDoc(docRef);
  } catch (error) {
    throw error;
  }
};

export const getDocuments = async (collectionName: string) => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    const documents: any[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Preserve Firestore document ID as documentId and internal id separately
      documents.push({ 
        ...data,
        documentId: doc.id, // Always preserve the actual Firestore document ID
        id: data.id || doc.id // Keep internal id field, fallback to documentId if not present
      });
    });
    return documents;
  } catch (error) {
    throw error;
  }
};

export const getDocumentsByQuery = async (collectionName: string, field: string, operator: any, value: any) => {
  try {
    const q = query(collection(db, collectionName), where(field, operator, value));
    const querySnapshot = await getDocs(q);
    const documents: any[] = [];
    querySnapshot.forEach((doc) => {
      documents.push({ id: doc.id, ...doc.data() });
    });
    return documents;
  } catch (error) {
    throw error;
  }
};

// Storage functions
export const uploadFile = async (file: File, path: string) => {
  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    throw error;
  }
};

// User management
export const createUser = async (userData: any) => {
  try {
    const userRef = doc(db, 'users', userData.id);
    await setDoc(userRef, {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw error;
  }
};

export const getUserById = async (userId: string) => {
  try {
    return await getDocument('users', userId);
  } catch (error) {
    throw error;
  }
};

export const getUserByEmail = async (email: string) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    // Return the first matching user
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error getting user by email:', error);
    throw error;
  }
};

export const updateUserUID = async (oldUserId: string, newUserId: string) => {
  try {
    // Get the existing user data
    const userData = await getUserById(oldUserId);
    if (!userData) {
      throw new Error(`User with ID ${oldUserId} not found`);
    }

    // Create new document with new UID
    const newUserRef = doc(db, 'users', newUserId);
    await setDoc(newUserRef, {
      ...userData,
      id: newUserId,
      updatedAt: serverTimestamp(),
      uidMigrated: true,
      previousUID: oldUserId
    });

    // Delete old document
    const oldUserRef = doc(db, 'users', oldUserId);
    await deleteDoc(oldUserRef);

    console.log(`✅ User UID migrated: ${oldUserId} → ${newUserId}`);
    
    // Return the new user data with updated ID
    return {
      ...userData,
      id: newUserId,
      uidMigrated: true,
      previousUID: oldUserId
    };
  } catch (error) {
    console.error('Error updating user UID:', error);
    throw error;
  }
};

// Helper function for fuzzy string matching
const calculateSimilarity = (str1: string, str2: string): number => {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 1.0;
  
  // Simple fuzzy matching - check if one contains the other or similar patterns
  if (s1.includes(s2) || s2.includes(s1)) return 0.8;
  
  // Check for common variations (dots, spaces, abbreviations)
  const normalize = (str: string) => str.replace(/[.\s-]/g, '').toLowerCase();
  const n1 = normalize(s1);
  const n2 = normalize(s2);
  
  if (n1 === n2) return 0.9;
  if (n1.includes(n2) || n2.includes(n1)) return 0.7;
  
  return 0.0;
};

// Find similar areas within a warehouse
export const findSimilarAreas = async (areaName: string, warehouseId: string): Promise<any[]> => {
  try {
    // Allow disabling similarity-based area suggestions via env flag
    const disableAreaSimilarity = process.env.REACT_APP_DISABLE_AREA_SIMILARITY === 'true' ||
                                  process.env.REACT_APP_DISABLE_SIMILARITY_MATCH === 'true';
    if (disableAreaSimilarity) {
      console.log('🛑 Area similarity matching disabled via REACT_APP_DISABLE_AREA_SIMILARITY/REACT_APP_DISABLE_SIMILARITY_MATCH');
      return [];
    }

    const areas = await getDocumentsByQuery('areas', 'warehouseId', '==', warehouseId);
    const activeAreas = areas.filter(area => area.isActive);
    
    const similarAreas = activeAreas.filter(area => {
      const areaNameLower = area.name.toLowerCase().trim();
      const suggestedAreaLower = areaName.toLowerCase().trim();
      
      // Multiple matching strategies (same as AddOutlet.tsx)
      const exactMatch = areaNameLower === suggestedAreaLower;
      const containsMatch = areaNameLower.includes(suggestedAreaLower) || suggestedAreaLower.includes(areaNameLower);
      
      // Clean matching - remove parentheses and normalize spaces
      const cleanAreaName = areaNameLower
        .replace(/\s*\(.*?\)\s*/g, ' ')  // Remove parentheses and contents
        .replace(/\s+/g, ' ')           // Normalize multiple spaces
        .trim();
      const cleanSuggestedName = suggestedAreaLower
        .replace(/\s*\(.*?\)\s*/g, ' ')  // Remove parentheses and contents  
        .replace(/\s+/g, ' ')           // Normalize multiple spaces
        .trim();
      const cleanMatch = cleanAreaName === cleanSuggestedName;
      
      // Extra fuzzy matching for common patterns like "West" vs "(West)"
      const fuzzyAreaName = areaNameLower
        .replace(/\s*\(([^)]+)\)\s*/g, ' $1 ')  // Convert "(West)" to " West "
        .replace(/\s+/g, ' ')                   // Normalize spaces
        .trim();
      const fuzzySuggestedName = suggestedAreaLower
        .replace(/\s*\(([^)]+)\)\s*/g, ' $1 ')  // Convert "(West)" to " West "
        .replace(/\s+/g, ' ')                   // Normalize spaces
        .trim();
      const fuzzyMatch = fuzzyAreaName === fuzzySuggestedName;
      
      // Fallback to similarity calculation for other cases
      const similarity = calculateSimilarity(area.name, areaName);
      const similarityMatch = similarity >= 0.6;
      
      const isMatch = exactMatch || containsMatch || cleanMatch || fuzzyMatch || similarityMatch;
      
      if (isMatch && (exactMatch || containsMatch || cleanMatch || fuzzyMatch)) {
        console.log('🎯 Firebase area match found:', {
          dbAreaName: area.name,
          suggestedName: areaName,
          matchType: exactMatch ? 'exact' : containsMatch ? 'contains' : cleanMatch ? 'clean' : fuzzyMatch ? 'fuzzy' : 'similarity'
        });
      }
      
      return isMatch;
    });
    
    return similarAreas;
  } catch (error) {
    console.error('Error finding similar areas:', error);
    return [];
  }
};

// Find similar localities within an area
export const findSimilarLocalities = async (localityName: string, areaId: string): Promise<any[]> => {
  try {
    // Allow disabling similarity-based locality suggestions via env flag
    const disableSimilarity = process.env.REACT_APP_DISABLE_LOCALITY_SIMILARITY === 'true' ||
                              process.env.REACT_APP_DISABLE_SIMILARITY_MATCH === 'true';
    if (disableSimilarity) {
      console.log('🛑 Locality similarity matching disabled via REACT_APP_DISABLE_LOCALITY_SIMILARITY');
      return [];
    }

    const localities = await getDocumentsByQuery('localities', 'areaId', '==', areaId);
    const activeLocalities = localities.filter(locality => locality.isActive);
    
    const similarLocalities = activeLocalities.filter(locality => {
      const similarity = calculateSimilarity(locality.name, localityName);
      return similarity >= 0.6; // Optimized threshold for better precision
    });
    
    return similarLocalities;
  } catch (error) {
    console.error('Error finding similar localities:', error);
    return [];
  }
};

// Create a new area from geocoded data
export const createAreaFromGeocode = async (areaName: string, warehouseFirebaseId: string, createdBy: string): Promise<string> => {
  try {
    // Get warehouse info for naming consistency and convert to custom ID
    const warehouse = await getDocument('warehouses', warehouseFirebaseId);
    const warehouseName = (warehouse as any)?.name || '';
    const customWarehouseId = (warehouse as any)?.warehouseId || warehouseFirebaseId; // Use custom ID
    
    // Attempt to get Place ID for the area
    let googlePlaceId: string | undefined;
    try {
      if (placeIdLookup.isAvailable()) {
        console.log(`🔍 Looking up Place ID for area: ${areaName}`);
        googlePlaceId = await placeIdLookup.getAreaPlaceId(areaName, 'Mumbai') || undefined;
        if (googlePlaceId) {
          console.log(`✅ Found Place ID for area "${areaName}": ${googlePlaceId}`);
        } else {
          console.log(`⚠️ No Place ID found for area: ${areaName}`);
        }
      }
    } catch (placeIdError) {
      console.warn('Failed to fetch Place ID for area:', areaName, placeIdError);
    }
    
    const areaData = {
      name: areaName,
      warehouseId: customWarehouseId, // Store custom warehouse ID (e.g., MUM-WH01)
      warehouseName: warehouseName,
      isActive: true,
      createdBy: createdBy,
      source: 'geocoding', // Mark as auto-created
      ...(googlePlaceId && { googlePlaceId }) // Add Place ID if found
    };
    
    const areaId = await createDocument('areas', areaData);
    return areaId;
  } catch (error) {
    console.error('Error creating area from geocode:', error);
    throw error;
  }
};

// Create a new locality from geocoded data
export const createLocalityFromGeocode = async (
  localityName: string, 
  areaId: string, 
  pincode: string, 
  createdBy: string
): Promise<string> => {
  try {
    // Get area info for naming consistency
    const area = await getDocument('areas', areaId);
    const areaName = (area as any)?.name || '';
    const cityId = (area as any)?.cityId || '';
    const cityName = (area as any)?.cityName || '';
    
    // Attempt to get Place ID for the locality
    let googlePlaceId: string | undefined;
    try {
      if (placeIdLookup.isAvailable()) {
        console.log(`🔍 Looking up Place ID for locality: ${localityName}`);
        googlePlaceId = await placeIdLookup.getLocalityPlaceId(localityName, areaName, 'Mumbai') || undefined;
        if (googlePlaceId) {
          console.log(`✅ Found Place ID for locality "${localityName}": ${googlePlaceId}`);
        } else {
          console.log(`⚠️ No Place ID found for locality: ${localityName}`);
        }
      }
    } catch (placeIdError) {
      console.warn('Failed to fetch Place ID for locality:', localityName, placeIdError);
    }
    
    const localityData = {
      name: localityName,
      areaId: areaId,
      areaName: areaName,
      cityId: cityId,
      cityName: cityName,
      pincode: pincode || null,
      isActive: true,
      createdBy: createdBy,
      source: 'geocoding', // Mark as auto-created
      ...(googlePlaceId && { googlePlaceId }) // Add Place ID if found
    };
    
    const localityId = await createDocument('localities', localityData);
    return localityId;
  } catch (error) {
    console.error('Error creating locality from geocode:', error);
    throw error;
  }
};

// Update locality with pincode if missing
export const updateLocalityPincode = async (localityId: string, pincode: string): Promise<void> => {
  try {
    const locality = await getDocument('localities', localityId);
    if (locality && !(locality as any).pincode && pincode) {
      await updateDocument('localities', localityId, { pincode: pincode });
    }
  } catch (error) {
    console.error('Error updating locality pincode:', error);
    throw error;
  }
};

// Serial Number Generation Functions
export const generateNextSerialNumber = async (): Promise<string> => {
  try {
    const counterRef = doc(db, 'counters', 'outletSerial');
    
    const newSerialNumber = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      
      let currentCount = 1;
      if (counterDoc.exists()) {
        currentCount = (counterDoc.data().count || 0) + 1;
      }
      
      // Update the counter
      transaction.set(counterRef, { 
        count: currentCount,
        lastUpdated: serverTimestamp()
      });
      
      // Format serial number as purely numeric
      const serialNumber = currentCount.toString();
      return serialNumber;
    });
    
    return newSerialNumber;
  } catch (error) {
    console.error('Error generating serial number:', error);
    throw error;
  }
};

// Create outlet with automatic sequential ID generation
export const createOutletWithSerial = async (outletData: any) => {
  try {
    // Generate sequential number atomically
    const sequentialId = await generateNextSerialNumber();
    
    // Create outlet with sequential ID in the id field
    const outletDataWithId = {
      ...outletData,
      id: sequentialId, // Set the id field to the sequential number
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'outlets'), outletDataWithId);
    
    return {
      documentId: docRef.id, // Firestore document ID
      outletId: sequentialId // User-friendly sequential ID
    };
  } catch (error) {
    console.error('Error creating outlet with sequential ID:', error);
    throw error;
  }
};

// Quick save outlet (draft mode) - only requires location
export const quickSaveOutlet = async (basicOutletData: any) => {
  try {
    // Generate sequential number atomically
    const sequentialId = await generateNextSerialNumber();
    
    // Create draft outlet with minimal data and sequential ID
    const draftOutletData = {
      ...basicOutletData,
      id: sequentialId, // Set the id field to the sequential number
      status: 'draft',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(collection(db, 'outlets'), draftOutletData);
    
    return {
      documentId: docRef.id, // Firestore document ID
      outletId: sequentialId // User-friendly sequential ID
    };
  } catch (error) {
    console.error('Error quick saving outlet:', error);
    throw error;
  }
};

// Export deleteField function for removing fields from documents
export { deleteField };
